import { useCallback, useEffect } from 'react';
import { trim } from 'lodash';
import isFQDN from 'validator/lib/isFQDN';
import { preauthBbkApiSlice } from '@bbkAdminRedux/rtkq/preauth.bbkApi.slice';
import BrightbackSession from '@bbkAdminUtils/brightback-session';
import { type PLANS } from '@bbkAdminRedux/rtkq/checkout.plans';
import {
  brightbackAuthSlice,
  isSignedIn,
} from '@bbkAdminRedux/rtkq/brightback-auth.slice';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';
import { LogInPaths } from '@bbkAdminComponents/log-in-flow/log-in-paths';

export enum IdentityProviderEnum {
  CHARGEBEE_IDP = 'chargebee-idp',
}

export type ChargebeeData = {
  first_name: string;
  last_name?: string;
  email: string;
  merchant_id: string;
  merchant_name: string;
  merchant_domain?: string;
  domain: string;
  site_id: string;
  site_name: string;
  subscription_plan: PLANS;
  application_type: 'production' | 'sandbox';
  api_key: string;
  company_exists_for_merchant_id: boolean;
  user_exists: boolean;
  idp?: IdentityProviderEnum.CHARGEBEE_IDP;
  cb_idp_base_url?: string;
};

export type CheckUsernameResponse = {
  valid: boolean;
  url: string;
  code?: string;
};

export const MARKETO_UTM_FIELDS_QUERY_PARAMS_TO_COOKIE = {
  utm_medium: { fieldName: 'lastUTMMedium', cookieName: 'cbr_medium' },
  utm_source: { fieldName: 'lastUTMSource', cookieName: 'cbr_source' },
  utm_keyword: { fieldName: 'lastUTMKeyword', cookieName: 'cbr_keyword' },
  utm_campaign: { fieldName: 'lastUTMCampaign', cookieName: 'cbr_campaign' },
  utm_term: { fieldName: 'lastUTMTerm', cookieName: 'cbr_term' },
  utm_content: { fieldName: 'lastUTMContent', cookieName: 'cbr_content' },
  gclid: { fieldName: 'uTMGCLID', cookieName: 'cbr_gclid' },
  utm_adgroupid: { fieldName: 'lastUTMAdgroup', cookieName: 'cbr_adgroupid' },
  utm_campaignid: {
    fieldName: 'lastUTMCampaignID',
    cookieName: 'cbr_campaignid',
  },
};

export type MarketoUtmFieldsType = {
  [field in keyof typeof MARKETO_UTM_FIELDS_QUERY_PARAMS_TO_COOKIE]?: string;
};

export enum CbIdpRegion {
  US = 'us',
  EU = 'eu',
  AP = 'ap',
}

export type BbkClientConfig = {
  rollbar_access_token: string;
  base_uri: string;
  base_retention_uri: string;
  event_api_endpoint: string;
  sync_api_endpoint: string;
  event_log_api_endpoint: string;
  sync_log_api_endpoint: string;
  is_prod: boolean;
  is_test: boolean;
  environment: string;
  debug?: boolean;

  // CB IDP config
  cbidp_base_uri: {
    [key in CbIdpRegion]: string;
  };
  cbidp_client_id: string;

  // AdminApp config
  cognito_user_pool_id: string;
  cognito_web_client_id: string;
  cognito_region: string;
  cognito_domain: string;
};
export const preauthCompanySlice = preauthBbkApiSlice.injectEndpoints({
  endpoints: (build) => ({
    getClientConfig: build.query<BbkClientConfig, void>({
      query: () => ({
        url: `/client_config`,
        params: {
          nonce: Date.now(),
        },
      }),
    }),
    cbidpCheckUsername: build.query<CheckUsernameResponse, { email: string }>({
      query: ({ email }) => ({
        url: '/api/v1/cbidp/check-username',
        params: {
          email,
        },
        method: 'POST',
      }),
    }),
    cbidpInitiateMigration: build.query<void, { email: string }>({
      query: ({ email }) => ({
        url: '/api/v1/multi-product/initiate-migration',
        params: {
          email,
        },
        method: 'POST',
      }),
    }),
    initiateProvisioning: build.mutation<
      { valid: boolean },
      { domain: string; email: string; company: string }
    >({
      query: ({ ...body }) => ({
        url: '/api/v1/multi-product/provision',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      }),
    }),
    enrichCompany: build.mutation<
      unknown,
      {
        company: string;
        monthly_cancels?: string;
        billing_system?: string;
        business_model?: string;
        industry_vertical?: string;
        phone?: string;
        additional_params?: string;
      }
    >({
      query: ({ ...body }) => ({
        url: '/api/v1/company/enrich',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      }),
    }),
    fetchCachedDataFromChargebee: build.query<ChargebeeData, { token: string }>(
      {
        query: ({ token }) => ({
          url: `/api/v1/company/fetch_cached_data_from_chargebee`,
          params: {
            cb_email: token,
          },
        }),
      }
    ),
    cacheCompanyCreation: build.mutation<
      unknown,
      {
        email: string;
        company_name: string;
        domain: string;
        additional_params?: string;
      }
    >({
      query: ({ ...body }) => ({
        url: '/api/v1/company/cache_company_creation',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      }),
    }),
    createCompanyFromCache: build.mutation<
      { companyKey: string; appKey: string },
      void
    >({
      query: () => ({
        url: '/api/v1/company_from_cache',
        method: 'POST',
      }),
    }),
    assignInvitation: build.mutation<
      unknown,
      { username: string; inviteCode: string }
    >({
      query: ({ username, inviteCode }) => ({
        url: `/assign_invitation?code=${inviteCode}&external_user_id=${username}`,
      }),
    }),
  }),
});
export const DOMAIN_REGEX =
  /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})*$/;

export const domainValidator = (domain: string): string => {
  let domainError = '';
  if (!domain || trim(domain).length === 0) {
    domainError = 'Please enter your company domain.';
  }

  if (!DOMAIN_REGEX.test(domain)) {
    domainError = 'Please enter a valid domain.';
  }
  return domainError;
};

export const extractDomain = (url: string): string =>
  url.replace(/^(?:https?:\/\/)?([^/]+).*/, '$1');

export const useBaseUriDomain = () => {
  const { data: clientConfig } =
    preauthCompanySlice.endpoints.getClientConfig.useQuery();

  return extractDomain(clientConfig?.base_uri ?? 'app.retention.chargebee.com');
};

// When entering the vanity domain only following cases are entertained
// cancel.freshly.com
// subscription.pret.com
// cancel.freshly.co.uk
// cancel.subdomain.freshly.co.uk

// Do not prefix www. or http://www. in vanity domain as it will add
// complexity in fetching root domain

export const customDomainValidator = (domain: string): string => {
  let domainError = '';
  if (!domain || trim(domain).length === 0) {
    domainError = 'Please enter your company domain.';
  }

  if (!isFQDN(domain)) {
    domainError = 'Please enter a valid custom domain.';
  }
  return domainError;
};

const getTimeRemaining = () =>
  (BrightbackSession.getAccessTokenExpiresIn() ?? 0) -
  BrightbackSession.getAccessTokenAgeInSeconds();

export const usePollAndRefreshToken = () => {
  const [doRefreshTokens$] =
    brightbackAuthSlice.endpoints.doRefreshTokens.useMutation();
  const [signOut$] = brightbackAuthSlice.endpoints.signOut.useMutation();

  const startRefreshTimer = useCallback((timeout?: number) => {
    if (timeout) {
      console.log(
        `usePollAndRefreshToken, set up timer to refresh token in ${timeout} seconds`
      );
    }
    setTimeout(
      async () => {
        if (isSignedIn()) {
          await doRefreshTokens$()
            .unwrap()
            .catch(async (err) => {
              GlobalLogger.error(err);
              await signOut$({ redirect: LogInPaths.signIn }).unwrap();
            });
        }
        const timeRemaining = getTimeRemaining();
        startRefreshTimer(timeRemaining);
      },
      // FIXME: refactor timer to enqueue immediately if timeout has ran out or close to the end (less then 60s)
      // run refresh 30 seconds before the token expires or retry every 10 seconds
      (timeout && timeout > 60 ? timeout - 30 : 10) * 1000
    );
  }, []);

  useEffect(() => {
    startRefreshTimer();
  }, [startRefreshTimer]);
};
